import { Breadcrumb, Input, Popconfirm, Space, Table, Tooltip } from "antd";
import { debounce } from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { getRequest, postRequest } from "../../../axios";
import ButtonField from "../../../common/htmlComponents/ButtonField";
import { ReactComponent as ArchiveRestore } from "../../../image/ArchiveRestore.svg";
import SearchIcon from "../../../image/SearchIcon.svg";
import {
  ErrorNotificationMsg,
  SuccessNotificationMsg,
} from "../../../utils/Notifications";

import { Col, Row } from "antd";
import "antd/dist/antd.min.css";
import "../../../styles/Main.scss";

const { Search } = Input;

const ArchivedPincodeList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [archivedPincodeList, setArchivedPincodeList] = useState([]);
  const [totalCount, settotalCount] = useState(0);
  const [selectedRow, setSelectedRow] = useState([]);
  const [reload, setReload] = useState(false);
  const [searchText, setSearchText] = useState("");

  // state initialisation
  const [state, setState] = useState({
    page: 1,
    ordering: "-id",
    pagesize: 10,
    search: "",
    total: 0,
  });

  // Fetch the list of archived pincodes on component mount or state change
  useEffect(() => {
    const getPincodeArchiveList = async () => {
      setLoading(true);

      try {
        let apiResponse = await getRequest("pincode-archive/", state);
        if (apiResponse.status) {
          // Sort the fetched data by ID in descending order
          const sortedData = apiResponse.data.results.sort((a, b) => {
            return b.id - a.id;
          });
          setArchivedPincodeList(sortedData);
          settotalCount(apiResponse.data.count);
        } else {
          ErrorNotificationMsg(t("error_in_archive_list"));
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };
    getPincodeArchiveList();
  }, [state, reload, t]);

  // Handle changes in the table (pagination, filters, sorter)
  const handleTableChange = async (pagination, filters, sorter, ordering) => {
    const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
    setState({
      ...state,
      page: pagination.current,
      pagesize: pagination.pageSize,
      ordering: orderBy,
    });

    setSelectedRow(selectedRow)
  };

  // Get the sorting enum value based on the sort field and order
  const GetSortingEnumValue = (sortField, sortOrder) => {
    let sortingEnumKey = "-id";

    if (sortField === "pincode_number" && sortOrder === "ascend") {
      sortingEnumKey = "pincode_number";
    }
    if (sortField === "pincode_number" && sortOrder === "descend") {
      sortingEnumKey = "-pincode_number";
    }
    if (sortField === "state_name" && sortOrder === "ascend") {
      sortingEnumKey = "state_name";
    }
    if (sortField === "state_name" && sortOrder === "descend") {
      sortingEnumKey = "-state_name";
    }
    if (sortField === "city_name" && sortOrder === "ascend") {
      sortingEnumKey = "city_name";
    }
    if (sortField === "city_name" && sortOrder === "descend") {
      sortingEnumKey = "-city_name";
    }

    if (sortField === "zone_name" && sortOrder === "ascend") {
      sortingEnumKey = "zone_id__zone_name";
    }
    if (sortField === "zone_name" && sortOrder === "descend") {
      sortingEnumKey = "-zone_id__zone_name";
    }

    return sortingEnumKey;
  };

  // Debounced search callback function to handle search input changes
  const searchCallbackDelayed = (event) => {
    const { value } = event.target;
    debounceLoadData(value, onSearch);
  };
  // Update the state with the search value
  const onSearch = (value) => {
    setState({
      ...state,
      search: value,
    });
  };

  // Debounced function to load data with a delay
  const debounceLoadData = debounce((value, onSearch) => {
    onSearch(value);
  }, 1000);
  // Row selection configuration for the table
  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {

    },
    onSelect: (record, selected, selectedRows) => {
      const updatedSelectedRowKeys = selected ? [...selectedRow, record.id] : selectedRow.filter(key => key !== record.id);
      setSelectedRow(updatedSelectedRowKeys);
    },
    onSelectAll: (selected, selectedRows, changeRows) => {
      const updatedSelectedRowKeys = selected ? [...selectedRow, ...changeRows.map(row => row.id)] : selectedRow.filter(key => !changeRows.find(row => row.id === key));
      setSelectedRow(updatedSelectedRowKeys);
    },
    selectedRowKeys: selectedRow,
  };

  // Handle restoring of archived pincodes
  const handleRestore = async (id = "") => {
    let ids = selectedRow;
    if (ids.length === 0) {
      ids.push(id);
    }
    try {
      let apiResponse = await postRequest("pincode-archive/", {
        deleted: ids,
      });
      if (apiResponse.data.success) {
        SuccessNotificationMsg(t("pincode_restore_successfully"));
        setReload(!reload);
        setSelectedRow([]);
        navigate("/pincode");
      } else {
        ErrorNotificationMsg(t("error_in_restore_pincode"));
      }
    } catch (error) {
      ErrorNotificationMsg(error);
    }
  };

  // Define the columns for the table
  const columns = [
    {
      title: "Pin Code",
      dataIndex: "pincode_number",
      key: "pincode_number",
      sorter: true,
    },
    {
      title: "City",
      dataIndex: "city_name",
      key: "city_name",
      sorter: true,
    },
    {
      title: "State",
      dataIndex: "state_name",
      key: "state_name",
      sorter: true,
    },

    {
      title: "Zone",
      dataIndex: "zone_name",
      key: "zone_name",
      sorter: true,
    },

    {
      title: "Action",
      fixed: "right",
      width: "100px",
      key: "operation",

      render: (text, record) => (
        <Space>
          <Tooltip
            color={colors}
            placement="right"
            title={
              <span style={{ color: "#fff" }}>
                Restore
              </span>
            }
          >
            <Popconfirm
              className="action"
              title={t("pop_restore_title")}
              okText={t("pop_ok")}
              cancelText={t("pop_cancel")}
              onConfirm={() => handleRestore(record.id)}
            >
              <ArchiveRestore className="ArchiverestorImg" />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];
  const colors = ["#094E8F"];
  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">

            <h3>{t("pl_archived_pincode")}</h3>


            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/dashboard">{t("home")}</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <Link to="/pincode">{t("pincode")}</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>{t("archiveList")}</Breadcrumb.Item>
            </Breadcrumb>
          </div>
          <Space>
            <div className="searchwrp">
              <Space direction="vertical">
              <Search
  value={searchText}
  onChange={(e) => {
    const value = e.target.value;
    setSearchText(value);
    debounceLoadData(value, onSearch);
  }}
  allowClear
  onSearch={(value) => {
    setSearchText(value);
    onSearch(value);
  }}
  prefix={
    <img
      src={SearchIcon}
      alt="SearchIcon"
      className="SearchIcon"
      width={22}
    />
  }
  placeholder={t("pl_search_list")}
  style={{ width: 250 }}
/>
              </Space>
            </div>
            {selectedRow.length > 0 && (
              <Popconfirm
                className="action"
                title={t("pop_restore_title")}
                okText={t("pop_ok")}
                cancelText={t("pop_cancel")}
                placement="left"
                onConfirm={() => handleRestore()}
              >
                <ButtonField
                  className="BlueShadowBtn"
                  type="primary"
                  htmlType="button"
                  label="Restore"
                />
              </Popconfirm>
            )}
          </Space>
        </div>
      </div>
<div className="stepscontentwrap">
        <div className="stepsContent">
      <section className="grid-sec mt-5 tablegridsec">
        <div className="PR-body" style={{padding:'0'}}>
          <Row className="tblheader">
            <Col
              xs={24}
              sm={24}
              md={12}
              lg={12}
              xl={12}
              className="tbl-heading"
            ></Col>
          </Row>
<div className="tablewrp">
          <Table
            rowSelection={rowSelection}
            rowKey={(record) => record.id}
            columns={columns}
            dataSource={archivedPincodeList}
            scroll={{ x: 800 }}
            loading={loading}
            pagination={{
              defaultCurrent: state.page,
              defaultPageSize: state.pagesize,
              total: totalCount,
              showSizeChanger: true,
              showTotal: (totalCount) => `Total ${totalCount} items`,
              locale: {
                items_per_page: 'Items / Page', // Text for the size changer
              },
            }}
            onChange={handleTableChange}
          />
       </div>
        </div>
      </section>
      </div>
         <div className="FormFooter Container">
            <ButtonField
              className="SaveGradiantBtn"
              type="primary"
              htmlType="button"
              label={t("pl_back_to_list")}
              onClick={() =>
                navigate(
                  `/pincode`
                )
              }
            //onClick={() => setCurrentIndex(0)}
            />
          </div>
      </div>
    </>
  );
};

export default ArchivedPincodeList;
